/**
 * Salon-timezone wall-clock helpers. The API stores/returns UTC instants (ISO-8601); the diary is
 * drawn in the salon's wall clock (default Europe/London). We resolve the offset with
 * `Intl.DateTimeFormat` rather than pulling in a tz library — the console keeps its dependency
 * surface small (the diary is the performance budget, not feature creep).
 *
 * Note: the en-US formats below are machine-parsing internals (date-part extraction,
 * weekday indexing) and must stay en-US regardless of display locale. Only the
 * user-facing `longDate` reads the presentation LOCALE from ./locale.ts.
 */

import { dateFormat } from './locale.js';

const partsCache = new Map<string, Intl.DateTimeFormat>();
function dtf(tz: string): Intl.DateTimeFormat {
  let f = partsCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsCache.set(tz, f);
  }
  return f;
}

interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallParts(instant: Date, tz: string): Wall {
  const map: Record<string, string> = {};
  for (const p of dtf(tz).formatToParts(instant)) map[p.type] = p.value;
  return {
    year: +map.year!,
    month: +map.month!,
    day: +map.day!,
    hour: +(map.hour === '24' ? '0' : map.hour!),
    minute: +map.minute!,
    second: +map.second!,
  };
}

/** Minutes from local midnight for an instant in `tz` (e.g. 13:05 → 785). */
export function wallMinutes(iso: string | Date, tz: string): number {
  const w = wallParts(typeof iso === 'string' ? new Date(iso) : iso, tz);
  return w.hour * 60 + w.minute + w.second / 60;
}

/** "YYYY-MM-DD" of an instant in `tz`. */
export function isoDateInZone(instant: Date, tz: string): string {
  const w = wallParts(instant, tz);
  return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}

/** Offset (ms) such that localWallTime = utc + offset, evaluated at `instant`. */
function offsetMs(instant: Date, tz: string): number {
  const w = wallParts(instant, tz);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - instant.getTime();
}

/** The UTC instant of local-midnight for a salon date (handles the DST offset). */
export function zonedDayStart(date: string, tz: string): Date {
  const guess = new Date(`${date}T00:00:00Z`).getTime();
  // Two passes converge across DST boundaries.
  let utc = guess - offsetMs(new Date(guess), tz);
  utc = guess - offsetMs(new Date(utc), tz);
  return new Date(utc);
}

/** [start, end) UTC instants covering a salon day — ready for the appointments query window. */
export function zonedDayRange(date: string, tz: string): { fromIso: string; toIso: string } {
  const start = zonedDayStart(date, tz);
  const end = zonedDayStart(addDays(date, 1), tz);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday, in the salon zone (matches WorkingHours.weekday). */
export function weekdayInZone(date: string, tz: string): number {
  const noon = new Date(`${date}T12:00:00Z`);
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(noon);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

/** 540 → "9:00", 785 → "1:05", 1080 → "5:00" — 12-hour, no meridiem (matches the diary design). */
export function clockLabel(minutesFromMidnight: number): string {
  const total = Math.round(minutesFromMidnight);
  const h24 = Math.floor(total / 60) % 24;
  const m = total % 60;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')}`;
}

/** Format an instant as a salon-zone clock label, e.g. "1:05". */
export function clockLabelAt(iso: string | Date, tz: string): string {
  return clockLabel(wallMinutes(iso, tz));
}

/** "9:30 – 11:00" for a booking span. */
export function spanLabel(startIso: string, endIso: string, tz: string): string {
  return `${clockLabelAt(startIso, tz)} – ${clockLabelAt(endIso, tz)}`;
}

/** Long editorial date for the diary header: "Thursday 12 June". */
export function longDate(date: string, tz: string): string {
  const noon = new Date(`${date}T12:00:00Z`);
  return dateFormat({
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(noon);
}

export function todayInZone(tz: string): string {
  return isoDateInZone(new Date(), tz);
}

/** Minutes between two instants (b − a), e.g. for ETA chips. */
export function minutesBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 60000);
}

/** Inverse of wallMinutes: the UTC instant (ISO) for a wall time on a salon date. */
export function wallMinutesToUtcIso(date: string, minutes: number, tz: string): string {
  return new Date(zonedDayStart(date, tz).getTime() + minutes * 60_000).toISOString();
}
